import type { Factor } from '@supabase/supabase-js'
import { supabasePulse } from './supabasePulse'

const MFA_VERIFIED_STORAGE_KEY = 'pulse_mfa_verified'

export type PulseMfaMethod = 'totp'

export interface PulseTotpEnrollmentState {
  factorId: string
  qrCode: string
  secret: string
}

export class PulseAuthenticatorAlreadyEnrolledError extends Error {
  constructor() {
    super('An authenticator app is already connected. Enter the current 6-digit code to verify your identity.')
    this.name = 'PulseAuthenticatorAlreadyEnrolledError'
  }
}

interface StoredMfaVerification {
  userId: string
  verifiedAt: string
}

let fallbackStoredVerification: StoredMfaVerification | null = null

function readStoredVerification(): StoredMfaVerification | null {
  if (typeof window === 'undefined') return fallbackStoredVerification

  try {
    const raw = window.localStorage.getItem(MFA_VERIFIED_STORAGE_KEY)
    if (!raw) return null

    const parsed = JSON.parse(raw) as Partial<StoredMfaVerification>
    if (typeof parsed.userId !== 'string' || typeof parsed.verifiedAt !== 'string') {
      return null
    }

    return {
      userId: parsed.userId,
      verifiedAt: parsed.verifiedAt,
    }
  } catch {
    return fallbackStoredVerification
  }
}

export function maskPulseEmailForDisplay(email: string | null | undefined): string {
  if (!email) return ''
  return email.replace(/^(.{2})(.*)(@.*)$/, '$1***$3')
}

export function markPulseMfaVerifiedForUser(userId: string): void {
  fallbackStoredVerification = {
    userId,
    verifiedAt: new Date().toISOString(),
  }

  if (typeof window === 'undefined') return

  try {
    window.localStorage.setItem(
      MFA_VERIFIED_STORAGE_KEY,
      JSON.stringify(fallbackStoredVerification),
    )
  } catch {
    // Storage can be unavailable in private modes or locked-down test runtimes.
  }
}

export function clearPulseMfaVerification(): void {
  fallbackStoredVerification = null

  if (typeof window === 'undefined') return

  try {
    window.localStorage.removeItem(MFA_VERIFIED_STORAGE_KEY)
  } catch {
    // Storage can be unavailable in private modes or locked-down test runtimes.
  }
}

export function isPulseMfaVerifiedForUser(userId: string | null | undefined): boolean {
  if (!userId) return false

  const stored = readStoredVerification()
  if (stored?.userId !== userId) {
    return false
  }

  const verifiedDate = new Date(stored.verifiedAt)
  const hoursSinceVerified = (Date.now() - verifiedDate.getTime()) / (1000 * 60 * 60)

  if (hoursSinceVerified > 24) {
    clearPulseMfaVerification()
    return false
  }

  return true
}

export function getPulseAuthenticatorQrDataUri(qrCode: string | null | undefined): string | null {
  if (!qrCode) return null

  const trimmedQrCode = qrCode.trim()

  if (
    trimmedQrCode.startsWith('data:') ||
    trimmedQrCode.startsWith('http://') ||
    trimmedQrCode.startsWith('https://')
  ) {
    return trimmedQrCode
  }

  if (trimmedQrCode.startsWith('<svg')) {
    return `data:image/svg+xml;utf-8,${encodeURIComponent(trimmedQrCode)}`
  }

  return trimmedQrCode
}

export async function fetchPulseVerifiedTotpFactor(): Promise<Factor<'totp', 'verified'> | null> {
  const { data, error } = await supabasePulse.auth.mfa.listFactors()

  if (error) throw error

  const verifiedFactor = data?.all.find(
    (factor): factor is Factor<'totp', 'verified'> =>
      factor.factor_type === 'totp' && factor.status === 'verified',
  )

  return verifiedFactor ?? data?.totp?.[0] ?? null
}

export function isPulseAuthenticatorAlreadyEnrolledError(error: unknown): boolean {
  if (error instanceof PulseAuthenticatorAlreadyEnrolledError) {
    return true
  }

  const message = error instanceof Error ? error.message : String(error)
  const normalizedMessage = message.toLowerCase()

  return (
    normalizedMessage.includes('already exists') &&
    (normalizedMessage.includes('friendly name') || normalizedMessage.includes('factor'))
  )
}

export async function enrollPulseAuthenticatorApp(): Promise<PulseTotpEnrollmentState> {
  async function unenrollUnverifiedTotpFactors(): Promise<void> {
    const { data: factorData, error: factorError } = await supabasePulse.auth.mfa.listFactors()

    if (factorError) throw factorError

    const verifiedFactor = factorData?.all.find(
      (factor): factor is Factor<'totp', 'verified'> =>
        factor.factor_type === 'totp' && factor.status === 'verified',
    )

    if (verifiedFactor) {
      throw new PulseAuthenticatorAlreadyEnrolledError()
    }

    const pendingFactors = factorData?.all.filter(
      (factor): factor is Factor<'totp'> =>
        factor.factor_type === 'totp' && factor.status === 'unverified',
    ) ?? []

    for (const pendingFactor of pendingFactors) {
      const { error: unenrollError } = await supabasePulse.auth.mfa.unenroll({
        factorId: pendingFactor.id,
      })

      if (unenrollError) throw unenrollError
    }
  }

  async function enroll(): Promise<PulseTotpEnrollmentState> {
    const { data, error } = await supabasePulse.auth.mfa.enroll({
      factorType: 'totp',
      friendlyName: 'PULSE Authenticator',
      issuer: 'PULSE',
    })

    if (error) throw error

    return {
      factorId: data.id,
      qrCode: data.totp.qr_code,
      secret: data.totp.secret,
    }
  }

  await unenrollUnverifiedTotpFactors()

  try {
    return await enroll()
  } catch (error) {
    if (!isPulseAuthenticatorAlreadyEnrolledError(error)) {
      throw error
    }

    await unenrollUnverifiedTotpFactors()

    try {
      return await enroll()
    } catch (retryError) {
      if (isPulseAuthenticatorAlreadyEnrolledError(retryError)) {
        throw new PulseAuthenticatorAlreadyEnrolledError()
      }

      throw retryError
    }
  }
}
